// MODEL-KEYS-PLANS: the fake of Connections › API keys › Model access (`modelAccess.get` /
// `modelAccess.put`, services/api/src/routes/model-access.ts). The plan's permission comes from the
// billing twin (Plans › "Own model keys"), the company keys from the subscriptions twin (one live
// key per provider; only the last four are shown).
import type { OperationOutput } from "@companyos/sdk";
import type { Handler, Principal } from "./fake";
import { json, notFound, problem } from "./fake";

type View = OperationOutput<"modelAccess.get">;
type Key = { id: string; provider: string; label: string; key_last4: string | null };

export function createModelAccessHandlers(deps: {
  now: () => Date;
  planOf: (companyId: string) => { planKey: string | null; allowed: boolean };
  keysOf: (p: Principal) => Key[];
}): Record<string, Handler> {
  const view = (p: Principal): View => {
    const stored = (
      p.company.settings as { model_access?: { mode?: string; fallback_to_ours?: boolean } }
    ).model_access;
    const mode = stored?.mode === "own" ? "own" : "ours";
    const plan = deps.planOf(p.company.id);
    const seen = new Set<string>();
    return {
      mode,
      fallback_to_ours: stored?.fallback_to_ours === true,
      allowed: plan.allowed,
      plan_key: plan.planKey,
      effective: mode === "own" && plan.allowed ? "own" : "ours",
      providers: deps
        .keysOf(p)
        .filter((k) => !seen.has(k.provider) && seen.add(k.provider))
        .map((k) => ({
          provider: k.provider,
          credential_id: k.id,
          label: k.label,
          key_last4: k.key_last4,
        })),
      needed: ["anthropic", "deepseek"],
    };
  };
  return {
    "modelAccess.get": ({ principal }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      return json(view(principal));
    },
    "modelAccess.put": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      const b = (body ?? {}) as { mode?: unknown; fallback_to_ours?: unknown };
      if ((b.mode !== "ours" && b.mode !== "own") || typeof b.fallback_to_ours !== "boolean")
        return problem(422, "validation_failed", "Validation failed");
      const before = view(principal);
      if (b.mode === "own" && before.mode !== "own" && !before.allowed)
        return problem(
          403,
          "forbidden",
          "Forbidden",
          "Your plan does not include bringing your own model keys.",
        );
      Object.assign(principal.company, {
        settings: {
          ...principal.company.settings,
          model_access: { mode: b.mode, fallback_to_ours: b.fallback_to_ours },
        },
        updated_at: deps.now().toISOString(),
      });
      return json(view(principal));
    },
  };
}
